import {ColorType, colorValues, SelectableColors} from "../../components/JourneyMapEditor/ColorSelector";
import {LexicalUtil} from "../../utils/LexicalUtil";
import Lexical from "lexical";
import {AlignmentType} from "../../components/JourneyMapEditor/CellEditor/EditorCommon";

export type CHANGE_TEXT_STYLE_TYPE = 'font' | 'size' | 'color' | 'align';
export type TOGGLE_TEXT_STYLE_TYPE = 'bold';
export type TEXT_STYLE_TYPE = CHANGE_TEXT_STYLE_TYPE | TOGGLE_TEXT_STYLE_TYPE;

function lookupOrDefault<T, U>(string: string, map: { [key: string]: T }, _default: U): T | U {
    if (map[string]) {
        return map[string]
    }
    return _default;
}

function lookup<T>(value: string, map: { [key: string]: T }): T {
    if (map[value]) {
        return map[value]
    }

    throw new Error(`not found: ${value}`);
}

abstract class _TextStyleType<Type> {
    protected readonly type: Type;

    protected constructor(type: Type) {
        this.type = type;
    }

    abstract toCssProperty(): string;

    abstract toCssValue(): string;

    toCssString(): string {
        return `${this.toCssProperty()}: ${this.toCssValue()};`;
    }

}

export type ALIGN_STYLE_TYPE = 'align_left' | 'align_center' | 'align_right';
export type SHORT_ALIGN_STYLE_TYPE = 'left' | 'center' | 'right';

export class AlignStyleType extends _TextStyleType<ALIGN_STYLE_TYPE> {

    toCssProperty(): string {
        return 'text-align';
    }

    toCssValue(): string {
        return {
            'align_left': 'left',
            'align_center': 'center',
            'align_right': 'right',
        }[this.type];
    }

    toShortName(): string {
        return this.toCssValue();
    }

    toAlignmentTypeString(): AlignmentType {
        return this.type;
    }

    toLexicalElementNodeFormat(): number {
        return {
            'align_left': LexicalUtil.IS_ALIGN_LEFT,
            'align_center': LexicalUtil.IS_ALIGN_CENTER,
            'align_right': LexicalUtil.IS_ALIGN_RIGHT,
        }[this.type];
    }

    toLexicalElementFormatType(): Lexical.ElementFormatType {
        return ({
            'align_left': 'left',
            'align_center': 'center',
            'align_right': 'right',
        } as const)[this.type];
    }

    private static instance: { [key: string]: AlignStyleType } = {
        'align_left': new AlignStyleType('align_left'),
        'align_center': new AlignStyleType('align_center'),
        'align_right': new AlignStyleType('align_right'),
    };

    static fromString(string: string): AlignStyleType {
        return lookup(string, AlignStyleType.instance);
    }

    static fromStringOrDefault<T>(string: string, _default: T): AlignStyleType | T {
        return lookupOrDefault(string, AlignStyleType.instance, _default);
    }

    static fromShortName(short_name: SHORT_ALIGN_STYLE_TYPE): AlignStyleType {
        const name = lookup(short_name, {
            'left': 'align_left',
            'center': 'align_center',
            'right': 'align_right',
        });

        return AlignStyleType.fromString(name);
    }

    static fromLexicalElementNodeOrDefault<T>(node: Lexical.ElementNode, _default: T): AlignStyleType | T {
        const node_format = node.__format;

        if (node_format === LexicalUtil.FORMAT_NONE) {
            return _default;
        } else if (node_format === LexicalUtil.IS_ALIGN_LEFT) {
            return AlignStyleType.fromString('align_left');
        } else if (node_format === LexicalUtil.IS_ALIGN_CENTER) {
            return AlignStyleType.fromString('align_center');
        } else if (node_format === LexicalUtil.IS_ALIGN_RIGHT) {
            return AlignStyleType.fromString('align_right');
        }

        throw new Error(`unexpected node_format: ${node_format}`);
    }

}


export type COLOR_STYLE_TYPE = `color_style-${ColorType}`;
export type SHORT_COLOR_STYLE_TYPE = ColorType;

function color2StyleType(color: ColorType): COLOR_STYLE_TYPE {
    return `color_style-${color}`;
}

export class ColorStyleType extends _TextStyleType<COLOR_STYLE_TYPE> {

    toCssProperty(): string {
        return 'color';
    }

    toCssValue(): string {
        return this.type.substring('color_style-'.length);
    }

    toShortName(): string {
        return this.toCssValue();
    }

    toLabel() {
        return this.type.substring('color_style-'.length);
    }

    private static instance: { [key: string]: ColorStyleType } = (() => {
        const instance: { [key: string]: ColorStyleType } = {};
        SelectableColors.forEach(color => {
            instance[color2StyleType(color)] = new ColorStyleType(color2StyleType(color));
        });
        return instance;
    })();

    private static color_type2color_style_type_map = (() => {
        const map: { [key: string]: COLOR_STYLE_TYPE } = {};
        SelectableColors.forEach(c => map[c] = color2StyleType(c));
        return map;
    })();

    static fromString(string: string): ColorStyleType {
        return lookup(string, ColorStyleType.instance);
    }

    static fromStringOrDefault<T>(string: string, _default: T): ColorStyleType | T {
        return lookupOrDefault(string, ColorStyleType.instance, _default);
    }

    static fromShortName(short_name: SHORT_COLOR_STYLE_TYPE): ColorStyleType {
        return ColorStyleType.fromString(lookup(short_name, ColorStyleType.color_type2color_style_type_map));
    }

    static fromCssValueOrDefault<T>(color: string, _default: T): ColorStyleType | T {

        let search = '';
        if (color.startsWith('#')) {
            search = color.toLowerCase();
        } else if (color.toLowerCase().startsWith('rgb(')) {
            search = color.replace(/rgb\((\d+), (\d+), (\d+)\)/,
                (m, p1, p2, p3) => '#' + Number.parseInt(p1).toString(16) + Number.parseInt(p2).toString(16) + Number.parseInt(p3).toString(16)
            ).toLowerCase();
        }

        const type = lookupOrDefault(search, ColorStyleType.color_type2color_style_type_map, undefined);
        return type === undefined ? _default : ColorStyleType.fromStringOrDefault(type, _default);
    }
}


export type FONT_STYLE_TYPE = 'font_style-serif' | 'font_style-sans_serif';
export type SHORT_FONT_STYLE_TYPE = 'serif' | 'sans_serif';

export class FontStyleType extends _TextStyleType<FONT_STYLE_TYPE> {

    toCssProperty(): string {
        return 'font-family';
    }

    toCssValue(): string {
        return {
            'font_style-serif': 'serif',
            'font_style-sans_serif': 'sans_serif',
        }[this.type];
    }

    toShortName(): string {
        return this.toCssValue();
    }

    toLabel(): string {
        return {
            'font_style-serif': '明朝',
            'font_style-sans_serif': 'ゴシック',
        }[this.type];
    }

    private static instance: { [key: string]: FontStyleType } = {
        'font_style-serif': new FontStyleType('font_style-serif'),
        'font_style-sans_serif': new FontStyleType('font_style-sans_serif'),
    };

    static fromString(string: string): FontStyleType {
        return lookup(string, FontStyleType.instance);
    }

    static fromStringOrDefault<T>(string: string, _default: T): FontStyleType | T {
        return lookupOrDefault(string, FontStyleType.instance, _default);
    }

    static fromShortName(short_name: SHORT_FONT_STYLE_TYPE): FontStyleType {
        return FontStyleType.fromString(lookup(short_name, {
            'serif': 'font_style-serif',
            'sans_serif': 'font_style-sans_serif',
        }));
    }

    static fromCssValueOrDefault<T>(value: string, _default: T): FontStyleType | T {
        const type = lookupOrDefault(value, {
            'serif': 'font_style-serif',
            'sans-serif': 'font_style-sans_serif',
        }, undefined);

        return type === undefined ? _default : FontStyleType.fromStringOrDefault(type, _default);
    }

}


export type SIZE_STYLE_TYPE = 'size_style-small' | 'size_style-normal' | 'size_style-medium' | 'size_style-large';
export type SHORT_SIZE_STYLE_TYPE = 'small' | 'normal' | 'medium' | 'large'

export class SizeStyleType extends _TextStyleType<SIZE_STYLE_TYPE> {

    toCssProperty(): string {
        return 'font-size';
    }

    toCssValue(): string {
        return {
            'size_style-small': '12px',
            'size_style-normal': '14px',
            'size_style-medium': '20px',
            'size_style-large': '24px',
        }[this.type];
    }

    toShortName(): string {
        return {
            'size_style-small': 'small',
            'size_style-normal': 'normal',
            'size_style-medium': 'medium',
            'size_style-large': 'large',
        }[this.type];
    }

    toLabel(): string {
        return {
            'size_style-small': '小',
            'size_style-normal': '標準',
            'size_style-medium': '大',
            'size_style-large': '特大',
        }[this.type];
    }

    private static instance: { [key: string]: SizeStyleType } = {
        'size_style-small': new SizeStyleType('size_style-small'),
        'size_style-normal': new SizeStyleType('size_style-normal'),
        'size_style-medium': new SizeStyleType('size_style-medium'),
        'size_style-large': new SizeStyleType('size_style-large'),
    };

    static fromString(string: string): SizeStyleType {
        return lookup(string, SizeStyleType.instance);
    }

    static fromStringOrDefault<T>(string: string, _default: T): SizeStyleType | T {
        return lookupOrDefault(string, SizeStyleType.instance, _default);
    }

    static fromShortName(short_name: SHORT_SIZE_STYLE_TYPE): SizeStyleType {
        return SizeStyleType.fromString(lookup(short_name, {
            'small': 'size_style-small',
            'normal': 'size_style-normal',
            'medium': 'size_style-medium',
            'large': 'size_style-large',
        }));
    }

    static fromCssValueOrDefault<T>(size: string, _default: T): SizeStyleType | T {
        const type = lookupOrDefault(size, {
            '12px': 'size_style-small',
            '14px': 'size_style-normal',
            '20px': 'size_style-medium',
            '24px': 'size_style-large',
        }, undefined);

        return (type === undefined) ? _default : SizeStyleType.fromStringOrDefault(type, _default);
    }
}


export type WEIGHT_STYLE_TYPE = 'weight_style-bold';
export type SHORT_WEIGHT_STYLE_TYPE = 'bold'

type BOLD_TYPE = boolean;


export class BoldType extends _TextStyleType<BOLD_TYPE> {

    toCssProperty(): string {
        return 'font-weight';
    }

    toCssValue(): string {
        return this.type ? 'bold' : 'normal';
    }

    toShortName(): string {
        return this.toCssValue();
    }

    toLexicalTextNodeFormat(): number {
        return this.type ? LexicalUtil.IS_BOLD : LexicalUtil.FORMAT_NONE;
    }

    isBold(): boolean {
        return this.type;
    }

    private static bold_instance = new BoldType(true);
    private static normal_instance = new BoldType(false);

    static bold(): BoldType {
        return BoldType.bold_instance;
    }

    static normal(): BoldType {
        return BoldType.normal_instance;
    }

    static fromString(string: string): BoldType {
        return string === 'font_weight-bold' ? BoldType.bold_instance : BoldType.normal_instance;
    }

    static fromShortName(short_name: SHORT_WEIGHT_STYLE_TYPE | undefined) {
        return short_name === 'bold' ? BoldType.bold_instance : BoldType.normal_instance;
    }

    static fromLexicalTextNode(node: Lexical.TextNode): BoldType {
        return (node.__format & LexicalUtil.IS_BOLD) ? BoldType.bold_instance : BoldType.normal_instance;
    }
}

export class TextStyleType {

    private readonly style_type: TEXT_STYLE_TYPE;

    private static readonly css_property_map = {
        'font': 'font-family',
        'size': 'font-size',
        'color': 'color',
        'bold': null, //'font-weight'
        'align': null, // 'text-align'
    };

    constructor(style_type: TEXT_STYLE_TYPE) {
        this.style_type = style_type;
    }

    toCssProperty(): string {
        const css = TextStyleType.css_property_map[this.style_type];
        if (css === null) {
            throw new Error(`css property not defined: ${this.style_type}`)
        }
        return css;
    }

    valueToStyleNameOrDefault<T>(style_value: string, default_value: T): string | T {
        try {
            return this.valueToStyleName(style_value)
        } catch {
            return default_value;
        }
    }

    valueToStyleName(style_value: string): string {
        let map: { [key: string]: string } = {};
        if (this.style_type === 'font') {
            map = {
                'sans-serif': 'sans_serif',
                'serif': 'serif',
            };
        }
        if (this.style_type === 'size') {
            map = {
                '12px': 'small',
                '14px': 'normal',
                '20px': 'medium',
                '24px': 'large',
            };
        }
        if (this.style_type === 'color') {
            map = colorValues();
        }

        if (map[style_value]) {
            return map[style_value]
        }

        throw new Error(`style_value: ${style_value} in ${this.style_type} is not defined`);

    }

    styleNameToValue(style_name: string): string {
        let map: { [key: string]: string } = {};
        if (this.style_type === 'font') {
            map = {
                'sans-serif': 'sans-serif', // header toolbar の指定ミスで 'sans-serif' となっていることがあった
                'sans_serif': 'sans-serif',
                'serif': 'serif',
            };
        }
        if (this.style_type === 'size') {
            map = {
                'small': '12px',
                'normal': '14px',
                'medium': '20px',
                'large': '24px',
            };
        }
        if (this.style_type === 'color') {
            map = colorValues();
        }

        if (map[style_name]) {
            return map[style_name]
        }

        throw new Error(`style_name: ${style_name} in ${this.style_type} is not defined`);
    }

    makeCssString(value: string): string {
        return `${this.toCssProperty()}: ${this.styleNameToValue(value)};`;
    }

    makeCssObject(value: string): { [key: string]: string; } {
        const prop = this.toCssProperty();

        let obj = {} as { [key: string]: string; };
        obj[prop] = this.styleNameToValue(value);
        return obj;
    }
}
